/**
 * WireframeLM Web Components
 *
 * Semantic UI components that provide value beyond standard HTML.
 * Layout and structure should use standard HTML + Tailwind classes.
 */

// wf-icon: Material Symbols icon
class WfIcon extends HTMLElement {
  connectedCallback() {
    const name = this.getAttribute('name')
    const size = this.getAttribute('size')
    const fill = this.getAttribute('fill') === 'true'
    const weight = this.getAttribute('weight') || '400'

    if (!name) {
      console.warn('wf-icon requires a "name" attribute')
      return
    }

    this.textContent = name
    this.style.fontFamily = 'Material Symbols Outlined'
    this.style.fontWeight = weight
    this.style.fontStyle = 'normal'
    this.style.display = 'inline-block'
    this.style.lineHeight = '1'
    this.style.textTransform = 'none'
    this.style.letterSpacing = 'normal'
    this.style.wordWrap = 'normal'
    this.style.whiteSpace = 'nowrap'
    this.style.direction = 'ltr'
    this.style.userSelect = 'none'

    if (size) {
      switch (size) {
        case 'xs': this.style.fontSize = '16px'; break
        case 'sm': this.style.fontSize = '20px'; break
        case 'md': this.style.fontSize = '24px'; break
        case 'lg': this.style.fontSize = '32px'; break
        case 'xl': this.style.fontSize = '48px'; break
        default: this.style.fontSize = size + 'px'
      }
    } else {
      this.style.fontSize = '24px'
    }

    // Set variable font features for fill
    this.style.fontVariationSettings = fill ? '"FILL" 1' : '"FILL" 0'
  }
}

// wf-avatar: User avatar
class WfAvatar extends HTMLElement {
  connectedCallback() {
    const src = this.getAttribute('src')
    const label = this.getAttribute('label')
    const size = this.getAttribute('size') || 'md'
    const variant = this.getAttribute('variant') || 'circle'

    this.style.display = 'inline-flex'
    this.style.alignItems = 'center'
    this.style.justifyContent = 'center'
    this.style.flexShrink = '0'
    this.style.backgroundColor = '#e5e7eb'
    this.style.color = '#6b7280'
    this.style.fontWeight = '500'
    this.style.overflow = 'hidden'

    // Set size
    let sizeValue = '40px'
    switch (size) {
      case 'xs': sizeValue = '24px'; break
      case 'sm': sizeValue = '32px'; break
      case 'md': sizeValue = '40px'; break
      case 'lg': sizeValue = '48px'; break
      case 'xl': sizeValue = '64px'; break
      default: sizeValue = size
    }
    this.style.width = sizeValue
    this.style.height = sizeValue

    // Set variant
    if (variant === 'circle') {
      this.style.borderRadius = '50%'
    } else if (variant === 'rounded') {
      this.style.borderRadius = '8px'
    } else {
      this.style.borderRadius = '0'
    }

    if (src) {
      const img = document.createElement('img')
      img.src = src
      img.style.width = '100%'
      img.style.height = '100%'
      img.style.objectFit = 'cover'
      this.textContent = ''
      this.appendChild(img)
    } else if (label) {
      // Show initials from label
      const initials = label
        .split(' ')
        .map(word => word[0])
        .join('')
        .toUpperCase()
        .slice(0, 2)
      this.textContent = initials
    }
  }
}

// wf-badge: Badge or status indicator
class WfBadge extends HTMLElement {
  connectedCallback() {
    const label = this.getAttribute('label')
    const variant = this.getAttribute('variant') || 'neutral'
    const size = this.getAttribute('size') || 'md'

    if (label) {
      this.textContent = label
    }

    this.style.display = 'inline-flex'
    this.style.alignItems = 'center'
    this.style.justifyContent = 'center'
    this.style.fontWeight = '500'
    this.style.borderRadius = '12px'
    this.style.whiteSpace = 'nowrap'

    // Set size
    switch (size) {
      case 'sm':
        this.style.padding = '2px 8px'
        this.style.fontSize = '12px'
        break
      case 'md':
        this.style.padding = '4px 12px'
        this.style.fontSize = '14px'
        break
      case 'lg':
        this.style.padding = '6px 16px'
        this.style.fontSize = '16px'
        break
    }

    // Set variant colors
    switch (variant) {
      case 'primary':
        this.style.backgroundColor = '#dbeafe'
        this.style.color = '#1e40af'
        break
      case 'success':
        this.style.backgroundColor = '#d1fae5'
        this.style.color = '#065f46'
        break
      case 'warning':
        this.style.backgroundColor = '#fef3c7'
        this.style.color = '#92400e'
        break
      case 'danger':
        this.style.backgroundColor = '#fee2e2'
        this.style.color = '#991b1b'
        break
      case 'neutral':
      default:
        this.style.backgroundColor = '#f3f4f6'
        this.style.color = '#374151'
        break
    }
  }
}

// wf-divider: Visual separator
class WfDivider extends HTMLElement {
  connectedCallback() {
    const variant = this.getAttribute('variant') || 'solid'
    const orientation = this.getAttribute('orientation') || 'horizontal'

    this.style.display = 'block'
    this.style.border = 'none'
    this.style.margin = '0'

    if (orientation === 'horizontal') {
      this.style.width = '100%'
      this.style.height = '1px'
      this.style.borderTop = variant === 'dashed' ? '1px dashed #e5e7eb' : '1px solid #e5e7eb'
    } else {
      this.style.width = '1px'
      this.style.height = '100%'
      this.style.borderLeft = variant === 'dashed' ? '1px dashed #e5e7eb' : '1px solid #e5e7eb'
    }
  }
}

// Register all components
export function registerWireframeComponents() {
  // Semantic components only - use standard HTML + Tailwind for layout
  customElements.define('wf-icon', WfIcon)
  customElements.define('wf-avatar', WfAvatar)
  customElements.define('wf-badge', WfBadge)
  customElements.define('wf-divider', WfDivider)
}
